import { FileSpreadsheet, FileText, Truck, Users } from 'lucide-react'
import { ReportDownloadCard } from './ReportDownloadCard'
import { reportSectionSub, reportSectionTitle } from './ui'
import { ExecSummaryPreview, type ExecSummaryData } from './downloads/ExecSummaryPreview'
import { RoundTripPreview, type RoundTripData } from './downloads/RoundTripPreview'
import { BreakdownPreview, type BreakdownData } from './downloads/BreakdownPreview'
import { DriverPreview, type DriverData } from './downloads/DriverPreview'

/**
 * Every downloadable report in one place: the one-page PDF for leadership, and the three
 * detailed Excel workbooks for whoever runs the operation day to day. Weekly or monthly,
 * through an optional date, on demand -- nothing here is a standing subscription.
 *
 * Each card can preview its own data as a GUI dashboard before anyone downloads anything
 * -- the preview reuses the exact same backend function the download itself builds from
 * (see the *-preview endpoints in serving/app.py), so what's on screen and what lands in
 * the file never disagree.
 */
export function ExecSummaryDownload(): JSX.Element {
  return (
    <div className="mx-auto max-w-4xl p-6">
      <h1 className={reportSectionTitle}>Downloads</h1>
      <p className={reportSectionSub}>
        Executive summary and the detailed round trip, breakdown, and driver reports —
        weekly or monthly, generated on demand. Preview any of them before downloading.
      </p>

      <div className="mt-5 flex flex-col gap-4">
        <ReportDownloadCard<ExecSummaryData>
          icon={<FileText size={18} className="text-slate-600" />}
          title="Corridor executive summary"
          description="Dhar → Jhulwania → Dhule, one page, same numbers as the rest of Reports."
          endpoint="/api/reports/exec-summary.pdf"
          filenamePrefix="transvolt-corridor"
          extension="pdf"
          previewEndpoint="/api/reports/exec-summary-preview"
          renderPreview={(data) => <ExecSummaryPreview data={data} />}
        />
        <ReportDownloadCard<RoundTripData>
          icon={<Truck size={18} className="text-blue-600" />}
          iconTone="blue"
          title="Round trip report"
          description="Every round trip in the period, with turn-around time and the corridor's own median figures at the top."
          endpoint="/api/reports/round-trip-report.xlsx"
          filenamePrefix="transvolt-round-trip"
          extension="xlsx"
          previewEndpoint="/api/reports/round-trip-preview"
          renderPreview={(data) => <RoundTripPreview data={data} />}
          periods={['daily', 'weekly', 'monthly']}
        />
        <ReportDownloadCard<BreakdownData>
          icon={<FileSpreadsheet size={18} className="text-amber-600" />}
          iconTone="amber"
          title="Breakdown report"
          description="The working log — every incident, reported to resolved — plus a summary by fault category and severity, and trip/revenue loss."
          endpoint="/api/reports/breakdown-report.xlsx"
          filenamePrefix="transvolt-breakdown"
          extension="xlsx"
          previewEndpoint="/api/breakdowns/snapshot"
          renderPreview={(data) => <BreakdownPreview data={data} />}
          periods={['daily', 'weekly', 'monthly']}
        />
        <ReportDownloadCard<DriverData>
          icon={<Users size={18} className="text-violet-600" />}
          iconTone="violet"
          title="Driver report"
          description="Trips, driving hours, and critical events (overspeed, harsh braking, night driving) per driver, from the TIMS register."
          endpoint="/api/reports/driver-report.xlsx"
          filenamePrefix="transvolt-driver"
          extension="xlsx"
          previewEndpoint="/api/reports/driver-preview"
          renderPreview={(data) => <DriverPreview data={data} />}
          periods={['daily', 'weekly', 'monthly']}
        />
      </div>
    </div>
  )
}
